import React from "react";
import { PaymentsOutlined, ExpandMore } from "@mui/icons-material";
import { caseConstants } from "@_constants/case.constants";
import { useActivityExpand } from "../hooks/useActivityExpand";
import CardShell from "../core/CardShell";
import styles from "../CaseActivityCard.module.css";

// ── Type label map ─────────────────────────────────────────────────────────────

const TYPE_LABELS = {
  [caseConstants.CASEACTIVITY_TYPE_PAYMENT_SUCCEEDED]: "Payment succeeded",
  [caseConstants.CASEACTIVITY_TYPE_PAYMENT_FAILED]: "Payment failed",
  [caseConstants.CASEACTIVITY_TYPE_PAYMENT_INITIATED]: "Payment initiated",
  [caseConstants.CASEACTIVITY_TYPE_PAYMENT_RECEIVED]: "Payment received",
};

// ── Expanded billing detail (PAYMENT_RECEIVED only) ───────────────────────────

function PaymentReceivedDetail({ activity }) {
  const addr = activity.content?.billing_address ?? {};
  return (
    <div className={styles.payment_received_container}>
      <div className={styles.payment_received_company_info_block}>
        <div>
          <strong className={styles.payment_received_email_block_label}>
            Company:
          </strong>{" "}
          {addr.name ?? ""}
        </div>
        <div>
          <strong className={styles.payment_received_email_block_label}>
            VAT ID:
          </strong>{" "}
          {addr.vat_id ?? ""}
        </div>
        <div>
          <strong className={styles.payment_received_email_block_label}>
            Email:
          </strong>{" "}
          {addr.email ?? "-"}
        </div>
      </div>
      <div className={styles.payment_received_address_block}>
        <strong>Billing address</strong>
        <div>{addr.address_line_1 ?? ""}</div>
        <div>{addr.address_line_2 ?? ""}</div>
        <div>
          {addr.postal_code ? `${addr.postal_code} - ` : ""}
          {addr.city ?? ""}
        </div>
        <div>{addr.country ?? ""}</div>
      </div>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * PaymentActivityCard
 *
 * Renders all payment-related activity types.
 * PAYMENT_RECEIVED is expandable and shows billing address detail on expand.
 * All other payment types show a static summary.
 */
export default function PaymentActivityCard({ activity }) {
  const { detailsOpen, animateExpand, toggle } = useActivityExpand();
  const isReceived =
    activity.type === caseConstants.CASEACTIVITY_TYPE_PAYMENT_RECEIVED;

  // ── Content preview ────────────────────────────────────────────────────────

  const contentPreview = (() => {
    switch (activity.type) {
      case caseConstants.CASEACTIVITY_TYPE_PAYMENT_FAILED:
        return <>{activity.content.message}</>;

      case caseConstants.CASEACTIVITY_TYPE_PAYMENT_RECEIVED:
        return (
          <>
            <div>
              <strong>
                Total: {activity.content.amount} {activity.content.currency}
              </strong>
            </div>
            <div className={styles.content_secondary_line}>
              payment method: {activity.content.payment_method ?? "Unknown"}
            </div>
          </>
        );

      default:
        return null;
    }
  })();

  const openerColumn = isReceived ? (
    <div className={styles.opener_expand_wrap} onClick={toggle}>
      <ExpandMore
        className={`${styles.opener_expand_more} ${detailsOpen ? styles.gray : ""}`}
      />
    </div>
  ) : null;

  return (
    <CardShell
      activity={activity}
      animateExpand={animateExpand}
      icon={<PaymentsOutlined />}
      typeText={TYPE_LABELS[activity.type]}
      typeSubText={activity.user_name}
      contentPreview={contentPreview}
      openerColumn={openerColumn}
      onContentClick={isReceived ? toggle : undefined}
      hasOpener={isReceived}
    >
      {isReceived && detailsOpen && (
        <PaymentReceivedDetail activity={activity} />
      )}
    </CardShell>
  );
}
